import { useRef } from "react";

const SixDigitCodeInput = ({
  label,
  value,
  onChange,
  disabled = false,
  autoFocus = false,
  idPrefix,
}) => {
  const inputs = useRef([]);
  const digits = value.padEnd(6, " ").slice(0, 6);

  const setDigit = (index, input) => {
    const enteredDigits = input.replace(/\D/g, "");
    const next = digits.split("");

    if (enteredDigits.length > 1) {
      enteredDigits
        .slice(0, 6 - index)
        .split("")
        .forEach((digit, offset) => {
          next[index + offset] = digit;
        });
    } else {
      next[index] = enteredDigits || " ";
    }

    onChange(next.join("").replace(/\D/g, "").slice(0, 6));

    if (enteredDigits) {
      const nextIndex = Math.min(index + enteredDigits.length, 5);
      inputs.current[nextIndex]?.focus();
    }
  };

  const pasteDigits = (event, index) => {
    const pasted = event.clipboardData.getData("text").replace(/\D/g, "");
    if (!pasted) return;
    event.preventDefault();
    const next = digits.split("");
    pasted
      .slice(0, 6 - index)
      .split("")
      .forEach((digit, offset) => {
        next[index + offset] = digit;
      });
    onChange(next.join("").replace(/\D/g, "").slice(0, 6));
    inputs.current[Math.min(index + pasted.length, 5)]?.focus();
  };

  return (
    <fieldset
      className="space-y-2"
      disabled={disabled}
      data-1p-ignore="true"
      data-bwignore="true"
      data-lpignore="true"
    >
      <legend className="block text-sm font-medium text-zinc-700">
        {label}
      </legend>
      <div className="flex gap-2">
        {digits.split("").map((digit, index) => (
          <input
            key={index}
            ref={(element) => {
              inputs.current[index] = element;
            }}
            id={`${idPrefix}-${index + 1}`}
            aria-label={`${label} digit ${index + 1}`}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? "one-time-code" : "off"}
            pattern="[0-9]"
            maxLength={index === 0 ? 6 : 1}
            required
            autoFocus={autoFocus && index === 0}
            value={digit.trim()}
            onChange={(event) => setDigit(index, event.target.value)}
            onPaste={(event) => pasteDigits(event, index)}
            onKeyDown={(event) => {
              if (event.key === "Backspace" && !digit.trim() && index > 0)
                inputs.current[index - 1]?.focus();
              if (event.key === "ArrowLeft" && index > 0)
                inputs.current[index - 1]?.focus();
              if (event.key === "ArrowRight" && index < 5)
                inputs.current[index + 1]?.focus();
            }}
            className="h-12 w-11 rounded-md border border-zinc-300 bg-white text-center text-xl font-semibold tracking-wide text-zinc-900 shadow-sm outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-100 disabled:bg-zinc-100"
          />
        ))}
      </div>
    </fieldset>
  );
};

export default SixDigitCodeInput;
